import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api, { formatApiError } from "@/lib/api";
import { UserPlus, Phone, Mail, MapPin, Trash2, Users, ArrowLeft } from "lucide-react";

const KINDS = [
  { k: "dentist", name: "Dentist" },
  { k: "orthodontist", name: "Orthodontist" },
  { k: "ent", name: "ENT" },
  { k: "slp", name: "Speech-Language Pathologist" },
  { k: "myofunctional", name: "Myofunctional Therapist" },
  { k: "allergist", name: "Allergist" },
  { k: "sleep", name: "Sleep Specialist" },
  { k: "pediatrician", name: "Pediatrician" },
  { k: "other", name: "Other" },
];

export default function Providers() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ kind: "dentist", name: "", phone: "", email: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/providers").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setErr("");
    try {
      await api.post("/providers", form);
      setForm({ kind: "dentist", name: "", phone: "", email: "", address: "", notes: "" });
      setAdding(false);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this provider?")) return;
    await api.delete(`/providers/${id}`);
    await load();
  };

  return (
    <Shell>
      <Link to="/roadblocks" className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> Roadblocks</Link>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold flex items-center gap-2"><Users size={12} /> Referral List</div>
          <h1 className="font-display text-3xl font-bold text-navy mt-1">My trusted providers</h1>
          <p className="text-ink-mid mt-2">Save the local team your family already sees — dentist, ENT, SLP, and more — for one-tap reference when a roadblock needs an evaluation.</p>
        </div>
        <button onClick={() => setAdding((a) => !a)} data-testid="add-provider-btn" className="inline-flex items-center gap-2 bg-gold text-ink-dark px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-gold-light">
          <UserPlus size={14} /> Add provider
        </button>
      </div>

      {adding && (
        <div className="mb-8 bg-warm-white border border-edge rounded-3xl p-6" data-testid="provider-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Type</label>
              <select data-testid="provider-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark">
                {KINDS.map((k) => <option key={k.k} value={k.k}>{k.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Name / Practice</label>
              <input data-testid="provider-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Phone</label>
              <input data-testid="provider-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Email</label>
              <input data-testid="provider-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Address</label>
              <input data-testid="provider-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Notes</label>
              <textarea data-testid="provider-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream text-ink-dark" />
            </div>
          </div>
          {err && <div className="mt-3 text-sm text-red-700">{err}</div>}
          <div className="mt-4 flex gap-2 justify-end">
            <button onClick={() => setAdding(false)} className="text-ink-mid px-4 py-2">Cancel</button>
            <button data-testid="save-provider-btn" onClick={save} disabled={saving || !form.name.trim()} className="bg-gold text-ink-dark px-5 py-2 rounded-full font-semibold text-sm disabled:opacity-50">{saving ? "Saving…" : "Save provider"}</button>
          </div>
        </div>
      )}

      {list.length === 0 && !adding && (
        <div className="max-w-xl bg-warm-white border border-dashed border-edge rounded-3xl p-8 text-center" data-testid="providers-empty">
          <Users className="text-mauve mx-auto" size={28} />
          <div className="mt-4 font-display font-bold text-navy text-lg">Build your care team</div>
          <p className="text-ink-mid text-sm mt-2">Save the providers your family already sees so a referral is one tap away when the Roadblock Tracker suggests one.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {list.map((p) => {
          const kindName = KINDS.find((k) => k.k === p.kind)?.name || p.kind;
          return (
            <div key={p.id} className="bg-warm-white border border-edge rounded-2xl p-5" data-testid={`provider-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-mauve font-bold">{kindName}</div>
                  <div className="font-display font-bold text-navy text-lg mt-1">{p.name}</div>
                </div>
                <button onClick={() => remove(p.id)} data-testid={`remove-provider-${p.id}`} className="text-ink-soft hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-ink-mid">
                {p.phone && <a href={`tel:${p.phone}`} className="flex items-center gap-2 hover:text-navy"><Phone size={13} /> {p.phone}</a>}
                {p.email && <a href={`mailto:${p.email}`} className="flex items-center gap-2 hover:text-navy"><Mail size={13} /> {p.email}</a>}
                {p.address && <div className="flex items-start gap-2"><MapPin size={13} className="mt-0.5" /> <span>{p.address}</span></div>}
              </div>
              {p.notes && <div className="mt-3 text-sm text-ink-mid italic border-t border-edge pt-3">{p.notes}</div>}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
